import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Button,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';

const SnykRepoAlignmentTable = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [streamFilter, setStreamFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('repository');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await get('/api/snyk-mapping/repo-alignment', null, 'json', { useCache: false });
      if (response && response.success) {
        setMappings(response.mappings || []);
      } else {
        throw new Error(response?.error || 'Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching repo alignment:', err);
      setError(err.message || 'Failed to fetch repo alignment data');
    } finally {
      setLoading(false);
    }
  };

  const streams = useMemo(
    () => [...new Set(mappings.map(m => m.stream).filter(Boolean))].sort(),
    [mappings]
  );
  const teams = useMemo(
    () => [...new Set(mappings.map(m => m.team).filter(Boolean))].sort(),
    [mappings]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return mappings
      .filter(m => {
        if (streamFilter && m.stream !== streamFilter) return false;
        if (teamFilter === '__unassigned__') { if (m.team) return false; }
        else if (teamFilter && m.team !== teamFilter) return false;
        if (typeFilter && m.type !== typeFilter) return false;
        if (q && ![m.repository, m.team, m.stream, m.owner, m.em].some(v => v?.toLowerCase().includes(q))) return false;
        return true;
      })
      .sort((a, b) => {
        const av = (a[sortBy] || '').toLowerCase();
        const bv = (b[sortBy] || '').toLowerCase();
        return sortOrder === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
  }, [mappings, search, streamFilter, teamFilter, typeFilter, sortBy, sortOrder]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const handleSort = (col) => {
    setSortBy(prev => {
      if (prev === col) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
      else setSortOrder('asc');
      return col;
    });
    setPage(0);
  };

  const clearFilters = () => {
    setSearch('');
    setStreamFilter('');
    setTeamFilter('');
    setTypeFilter('');
    setPage(0);
  };

  const hasActiveFilters = !!(search || streamFilter || teamFilter || typeFilter);

  const sortProps = (col) => ({
    active: sortBy === col,
    direction: sortBy === col ? sortOrder : 'asc',
    onClick: () => handleSort(col)
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={fetchData}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Info banner */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        {mappings.length} repositories with team assignments. To make changes, use{' '}
        <strong>Admin → SNYK Mapping</strong>.
        <Chip label="Read-only" size="small" variant="outlined" sx={{ ml: 1 }} />
      </Alert>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              placeholder="Repo, team, owner, EM…"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Stream</InputLabel>
              <Select
                value={streamFilter}
                label="Stream"
                onChange={(e) => { setStreamFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Streams</MenuItem>
                {streams.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Team</InputLabel>
              <Select
                value={teamFilter}
                label="Team"
                onChange={(e) => { setTeamFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Teams</MenuItem>
                <MenuItem value="__unassigned__"><em>Unassigned</em></MenuItem>
                {teams.map(t => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="modern">Modern</MenuItem>
                <MenuItem value="legacy">Legacy</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              startIcon={<ClearIcon />}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {filtered.length} of {mappings.length} repositories{hasActiveFilters ? ' (filtered)' : ''}
      </Typography>

      {/* Table */}
      <Paper elevation={1}>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>
                  <TableSortLabel {...sortProps('repository')}><strong>Repository</strong></TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 140 }}>
                  <TableSortLabel {...sortProps('team')}><strong>Team</strong></TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 130 }}>
                  <TableSortLabel {...sortProps('stream')}><strong>Stream</strong></TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 130 }}>
                  <TableSortLabel {...sortProps('owner')}><strong>Owner</strong></TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 130 }}>
                  <TableSortLabel {...sortProps('em')}><strong>EM</strong></TableSortLabel>
                </TableCell>
                <TableCell sx={{ minWidth: 100 }}>
                  <TableSortLabel {...sortProps('type')}><strong>Type</strong></TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? 'No repositories match the current filters.'
                        : 'No repository data available.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row, idx) => (
                  <TableRow key={`${row.repository}-${idx}`} hover>
                    <TableCell sx={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
                      {row.repository}
                    </TableCell>
                    <TableCell>
                      {row.team
                        ? <Chip label={row.team} size="small" color="primary" variant="outlined" />
                        : <Typography variant="caption" color="text.disabled">Unassigned</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      {row.stream
                        ? <Chip label={row.stream} size="small" variant="outlined" />
                        : <Typography variant="caption" color="text.disabled">—</Typography>
                      }
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {row.owner || <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {row.em || <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.type || 'modern'}
                        size="small"
                        color={row.type === 'legacy' ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default SnykRepoAlignmentTable;
